/**
 * Pure filtering helpers for the partner directory. Kept free of React so the
 * URL and matching rules are unit-testable.
 */

export const PARTNER_DIRECTORY_PAGE_SIZE = 9;

export const PARTNER_FILTER_KEYS = [
  "stages",
  "industries",
  "countries",
  "categories",
] as const;

export type PartnerFilterKey = (typeof PARTNER_FILTER_KEYS)[number];

export interface PartnerFilters {
  query: string;
  stages: string[];
  industries: string[];
  countries: string[];
  categories: string[];
}

export interface FilterablePartner {
  name: string;
  summary: string;
  categories: readonly string[];
  growthStages: readonly string[];
  industries: readonly string[];
  countries: readonly string[];
}

export const EMPTY_PARTNER_FILTERS: PartnerFilters = {
  query: "",
  stages: [],
  industries: [],
  countries: [],
  categories: [],
};

function uniqueSorted(values: Iterable<string>): string[] {
  return [...new Set(values)].toSorted((a, b) => a.localeCompare(b));
}

export function hasActiveFilters(filters: PartnerFilters): boolean {
  return (
    filters.query.trim() !== "" ||
    PARTNER_FILTER_KEYS.some((key) => filters[key].length > 0)
  );
}

function matchesAny(selected: string[], values: readonly string[]): boolean {
  return selected.length === 0 || selected.some((s) => values.includes(s));
}

/** OR within a facet, AND across facets and the text query. */
export function filterPartners<T extends FilterablePartner>(
  partners: readonly T[],
  filters: PartnerFilters
): T[] {
  const query = filters.query.trim().toLowerCase();

  return partners.filter((partner) => {
    if (
      query !== "" &&
      ![partner.name, partner.summary, ...partner.categories].some((field) =>
        field.toLowerCase().includes(query)
      )
    ) {
      return false;
    }

    return (
      matchesAny(filters.stages, partner.growthStages) &&
      matchesAny(filters.industries, partner.industries) &&
      matchesAny(filters.countries, partner.countries) &&
      matchesAny(filters.categories, partner.categories)
    );
  });
}

export function getFacetOptions(
  partners: readonly FilterablePartner[]
): Record<PartnerFilterKey, string[]> {
  return {
    stages: uniqueSorted(partners.flatMap((p) => p.growthStages)),
    industries: uniqueSorted(partners.flatMap((p) => p.industries)),
    countries: uniqueSorted(partners.flatMap((p) => p.countries)),
    categories: uniqueSorted(partners.flatMap((p) => p.categories)),
  };
}

export interface RawPartnerFilters {
  q: string;
  stages: string[];
  industries: string[];
  countries: string[];
  categories: string[];
}

/**
 * Normalises URL state into filters. Values that are not real options are
 * dropped so stale or hand-edited URLs are harmless.
 */
export function sanitizePartnerFilters(
  raw: RawPartnerFilters,
  options: Record<PartnerFilterKey, string[]>
): PartnerFilters {
  const read = (key: PartnerFilterKey) =>
    uniqueSorted(raw[key].filter((value) => options[key].includes(value)));

  return {
    query: raw.q.slice(0, 100),
    stages: read("stages"),
    industries: read("industries"),
    countries: read("countries"),
    categories: read("categories"),
  };
}

export function toggleFilterValue(
  filters: PartnerFilters,
  key: PartnerFilterKey,
  value: string,
  checked: boolean
): PartnerFilters {
  const next = checked
    ? uniqueSorted([...filters[key], value])
    : filters[key].filter((v) => v !== value);
  return { ...filters, [key]: next };
}
